import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import AdminModal from "components/admin/AdminModal";
import {
  addToCoreCommunity,
  getEligibleCommunityUsers,
} from "apis/adminServices/userManagmentService";
import { User } from "./type";

interface AddCoreCommitteeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const PAGE_SIZE = 10;

const AddCoreCommitteeModal: React.FC<AddCoreCommitteeModalProps> = ({
  isOpen,
  onClose,
  onAdded,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchEligibleUsers = async (term: string, pageNumber: number) => {
    try {
      setLoading(true);
      const response = await getEligibleCommunityUsers(term, pageNumber, PAGE_SIZE);
      setUsers(response?.items ?? []);
      setTotalCount(response?.totalCount ?? 0);
    } catch (error) {
      console.error("Error fetching eligible users:", error);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchInput("");
      setSearchTerm("");
      setPage(1);
      setSelectedUserIds([]);
      setUsers([]);
      setTotalCount(0);
      setLoading(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchEligibleUsers(searchTerm, page);
    }
  }, [isOpen, searchTerm, page]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        setPage(1);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const pageUserIds = users.map((u) => u.userId);
  const allOnPageSelected =
    pageUserIds.length > 0 && pageUserIds.every((id) => selectedUserIds.includes(id));

  const togglePageSelection = () => {
    if (allOnPageSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !pageUserIds.includes(id)));
    } else {
      setSelectedUserIds((prev) =>
        Array.from(new Set([...prev, ...pageUserIds]))
      );
    }
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("Please select at least one user to add");
      return;
    }
    try {
      setSubmitting(true);
      const response = await addToCoreCommunity(selectedUserIds);

      const data = response?.data ?? {};
      const added: number[] = data.added ?? [];
      const alreadyInCommunity: number[] = data.alreadyInCommunity ?? [];
      const notFound: number[] = data.notFound ?? [];
      const blocked: number[] = data.blocked ?? [];

      if (added.length > 0) {
        toast.success(`${added.length} user(s) added to core committee`);
      }
      if (alreadyInCommunity.length > 0) {
        toast.info(
          `Already in committee: ${alreadyInCommunity.join(", ")}`
        );
      }
      if (notFound.length > 0) {
        toast.warn(`Not found: ${notFound.join(", ")}`);
      }
      if (blocked.length > 0) {
        toast.warn(`Cannot add admin user(s): ${blocked.join(", ")}`);
      }

      if (added.length > 0) {
        onAdded();
        onClose();
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      const status = error?.response?.status;
      if (apiMessage) {
        toast.error(apiMessage);
      } else if (status === 400) {
        toast.error("No user IDs provided.");
      } else if (status === 403) {
        toast.error("Cannot modify admin user(s).");
      } else if (status === 404) {
        toast.error("Selected user(s) not found.");
      } else if (status === 409) {
        toast.error("Selected user(s) already in core community.");
      } else {
        toast.error("Failed to add users to core committee");
      }
      console.error("Error adding users to core committee:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add to Core Committee"
      size="2xl"
      showCloseButton={true}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedUserIds.length === 0}
            className="px-6 py-2 text-sm bg-[#FFD700] text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Adding...</span>
              </>
            ) : (
              `Add${selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ""}`
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="relative">
          <input
            className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
            placeholder="Search by name or email"
            value={searchInput}
            onChange={handleSearchChange}
          />
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <i className="st-search text-lightgray-200"></i>
          </span>
        </div>

        <div className="relative overflow-x-auto sm:rounded-lg border border-gray-100">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 bg-blue-50">
              <tr>
                <th scope="col" className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    disabled={loading || users.length === 0}
                    onChange={togglePageSelection}
                    className="accent-primaryblue-50"
                  />
                </th>
                <th scope="col" className="px-4 py-3 text-lightgray-200">
                  User ID
                </th>
                <th scope="col" className="px-4 py-3 text-lightgray-200">
                  User Name
                </th>
                <th scope="col" className="px-4 py-3 text-lightgray-200">
                  Email Address
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-6 text-grease-100">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-6 text-grease-100">
                    No eligible users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const isSelected = selectedUserIds.includes(user.userId);
                  return (
                    <tr
                      key={user.userId}
                      className={`border-b cursor-pointer ${
                        isSelected ? "bg-blue-100" : "odd:bg-white even:bg-blue-50"
                      }`}
                      onClick={() => toggleUser(user.userId)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUser(user.userId)}
                          className="accent-primaryblue-50"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-grease-100 whitespace-nowrap">
                        {user.userId}
                      </td>
                      <td className="px-4 py-3 text-sm text-grease-100 whitespace-nowrap">
                        {user.username
                          ? `${user.username.charAt(0).toUpperCase()}${user.username.slice(1)}`
                          : "UserName"}
                      </td>
                      <td className="px-4 py-3 text-sm text-grease-100 whitespace-nowrap">
                        {user.email}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-lightgray-200">
              Showing {(page - 1) * PAGE_SIZE + 1}-
              {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              {selectedUserIds.length > 0
                ? ` • ${selectedUserIds.length} selected`
                : ""}
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-2 text-sm">
              <li>
                <button
                  disabled={page === 1 || loading}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    page === 1
                      ? "text-gray-300 border-gray-300"
                      : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setPage(page - 1)}
                >
                  <span className="sr-only">Previous</span>
                  <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                </button>
              </li>

              {generatePageNumbers().map((pageNum) => (
                <li key={pageNum}>
                  <button
                    disabled={loading}
                    className={`flex items-center justify-center w-7 h-7 rounded-md ${
                      page === pageNum
                        ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                        : "text-primaryblue-50 border border-primaryblue-50"
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li>
                <button
                  disabled={page === totalPages || loading}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    page === totalPages
                      ? "text-gray-300 border-gray-300"
                      : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setPage(page + 1)}
                >
                  <span className="sr-only">Next</span>
                  <i className="st-arrow-right text-xl font-semibold"></i>
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </AdminModal>
  );
};

export default AddCoreCommitteeModal;
